import type { HelperText } from '@bambi/component-library/src/components/forms/FormFieldHelperText/FormFieldHelperText';
import { FormFieldContainer } from '@bambi/component-library/src/components/forms/layout/FormFieldContainer';
import type { SupportedGridSizes } from '@bambi/component-library/src/components/forms/layout/FormFieldSet';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import type { InputHTMLAttributes } from 'react';
import React from 'react';

import { FormFieldBorderedCheckbox } from './FormFieldBorderedCheckbox';
import { FormFieldCheckbox } from './FormFieldCheckbox';
import { FormFieldDate } from './FormFieldDate';
import { FormFieldDefault } from './FormFieldDefault';
import { FormFieldGap } from './FormFieldGap';
import { FormFieldLocationTypeahead } from './FormFieldLocationTypeahead';
import { FormFieldSelect } from './FormFieldSelect';
import { FormFieldSwitch } from './FormFieldSwitch';
import { FormFieldTextArea } from './FormFieldTextArea';
import { FormFieldTime } from './FormFieldTime';

export type InputType =
  | 'text'
  | 'textarea'
  | 'date'
  | 'number'
  | 'email'
  | 'location-typeahead'
  | 'password'
  | 'bordered-checkbox'
  | 'checkbox'
  | 'select'
  | 'gap'
  | 'switch'
  | 'time'
  | 'color';

export type FormFieldProps<T> = {
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  label?: React.ReactNode;
  error?: string;
  helperText?: HelperText;
  type: InputType;
  options?: SelectOption[];
  colSpan?: SupportedGridSizes;
  value?: T;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: any) => void;
  className?: string;
  inline?: boolean;
  highlight?: boolean;
};

const formFieldComponentMap = {
  'bordered-checkbox': FormFieldBorderedCheckbox,
  select: FormFieldSelect,
  switch: FormFieldSwitch,
  textarea: FormFieldTextArea,
  'location-typeahead': FormFieldLocationTypeahead,
  default: FormFieldDefault,
  text: FormFieldDefault,
  number: FormFieldDefault,
  email: FormFieldDefault,
  password: FormFieldDefault,
  date: FormFieldDate,
  checkbox: FormFieldCheckbox,
  gap: FormFieldGap,
  time: FormFieldTime,
  color: FormFieldDefault,
};

type FormFieldComponentType<T> = React.ComponentType<
  FormFieldProps<T> & React.RefAttributes<HTMLInputElement>
>;

export const FormField = React.forwardRef(function _FormField<T>(
  {
    inputProps,
    label,
    error,
    value,
    helperText,
    type,
    options,
    colSpan,
    placeholder,
    disabled,
    onChange,
    inline,
    highlight,
  }: FormFieldProps<T>,
  forwardedRef: React.Ref<HTMLInputElement>
) {
  const FormFieldComponent = formFieldComponentMap[
    type
  ] as FormFieldComponentType<T>;

  return (
    <FormFieldContainer colSpan={colSpan}>
      <FormFieldComponent
        inputProps={inputProps}
        label={label ?? ''}
        error={error}
        helperText={helperText}
        type={type}
        options={options}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        ref={forwardedRef}
        inline={inline}
        highlight={highlight}
      />
    </FormFieldContainer>
  );
});
